import i18n from '@/i18n';
/**
 * 商品库条目 → TikTok 发品表单。
 *
 * 图片（对齐 TTS 商品图 + 变体图）：
 * - 各颜色主图 → 对应颜色变体图（skuImage）
 * - 第一个颜色的主图 → 商品图组主图（form.mainImage）
 * - 各色创作细节图 + 同色模板细节图 + 其余颜色主图 → 其他细节图（form.detailImages，合计受平台上限）
 * - 印花图进工作区（printImages），不上传平台（无 tiktokUrl）
 *
 * 标题须英文；描述沿用模板预设。颜色匹配时将商品库中文色名转英文再对模板。
 */

import {
  carouselUrlsForListingItem,
  detailImageUrlsForListingItem,
  printImageUrlsForListingItem,
  printImageNamesForListingItem,
  appendPrintNamesAfterHyphen,
  isGenericListingImageName,
  isOpaqueStorageBaseName,
  SHEIN_SUPPLIER_CODE_MAX_LEN,
} from './platformListingItemMedia';
import { resolveListingItemDisplayTitle } from './preferDownstreamListingAssets';
import { isManagedStorageUrl, resolveFetchableImageUrl, stripImageFileExtension } from '@/utils/imageUrlUtils';
import { toManagedCanonicalUploadsUrl } from '@/utils/imageDisplayAccess';
import { findImageSpaceRecordByUrl } from './imageCardMetaActions';
import { convertListingImageToTiktok } from './convertListingImageToTiktok';
import {
  ensureEnglishListingTitle,
  titleContainsCjk,
  TIKTOK_ENGLISH_TITLE_FAIL_MESSAGE,
} from './ensureEnglishListingTitle';
import {
  resolveListingSlotSkcIndex,
  extractColorFromImageNames,
  extractColorCodeFromSupplierSku,
  formatUnmatchedListingColorMessage,
} from './listingPatternColorMatch';
import { skuSizeKey } from '@/tiktok-platform/utils/tiktokSizeChart';
import {
  TIKTOK_DETAIL_IMAGES_MAX,
} from '@/tiktok-platform/utils/tiktokProductImage';
import { toTiktokSubmitColorName } from '@/tiktok-platform/utils/tiktokColorNameMap';
import {
  EMPTY_TIKTOK_PRINT_INFRINGEMENT,
  normalizeTiktokPrintInfringement,
} from '@/tiktok-platform/utils/tiktokPublishPrintInfringement';
import { getProductMeta, hydrateProductMetaFromItemRaw, resolveListingColorSlots } from './platformListingProductMeta';

/** 细节图上限（主图占 1 席，main_images 合计 ≤9） */
const MAX_DETAIL_IMAGES = TIKTOK_DETAIL_IMAGES_MAX;

/** TikTok 货号拼接印花名后上限（与 validateTiktokPublishForm 一致） */
export const TIKTOK_SUPPLIER_CODE_MAX_LEN = SHEIN_SUPPLIER_CODE_MAX_LEN;

export const LISTING_NO_PUBLISHABLE_COLOR_MESSAGE =
  '没有可推送的颜色：请确保已接收颜色能匹配模板主规格，且该颜色含主图';

/** 商品库行 → TikTok 发品表单印花侵权检测快照 */
export function resolveListingPrintInfringementForTiktok(listingItem) {
  if (!listingItem || listingItem.id == null) {
    return { ...EMPTY_TIKTOK_PRINT_INFRINGEMENT };
  }
  hydrateProductMetaFromItemRaw(listingItem);
  const meta = listingItem.id != null ? (getProductMeta(listingItem.id) || {}) : {};
  const raw = listingItem?.raw && typeof listingItem.raw === 'object' ? listingItem.raw : {};
  const rawRisk = String(raw?.risk || '').trim().toLowerCase();
  const effectiveRisk = (meta.risk && meta.risk !== 'pending')
    ? meta.risk
    : (rawRisk && rawRisk !== 'pending' ? rawRisk : meta.risk);
  const rawAdvice = String(raw?.riskAdvice || '').trim();
  const rawElements = Array.isArray(raw?.riskElements)
    ? raw.riskElements.map((el) => String(el || '').trim()).filter(Boolean)
    : [];
  const effectiveAdvice = meta.riskAdvice
    || rawAdvice
    || (rawElements.length ? rawElements.join('、') : '');
  const effectiveAt = String(meta.assessedAt || raw?.assessedAt || '').trim();
  const assessing = Boolean(meta.assessing);
  const hasResult = Boolean(effectiveAdvice)
    || (effectiveRisk && effectiveRisk !== 'pending' && !assessing);
  if (!hasResult) {
    return { ...EMPTY_TIKTOK_PRINT_INFRINGEMENT };
  }
  return normalizeTiktokPrintInfringement({
    risk: effectiveRisk || 'pending',
    assessing,
    riskAdvice: effectiveAdvice,
    assessedAt: effectiveAt,
  });
}

function toAbsoluteUrl(url) {
  const u = String(url || '').trim();
  if (!u) return '';
  if (/^(data:|blob:|https?:)/i.test(u)) return u;
  if (u.startsWith('//')) return `https:${u}`;
  if (typeof window !== 'undefined' && u.startsWith('/')) {
    return `${window.location.origin}${u}`;
  }
  return u;
}

function fileNameFromUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const path = new URL(raw, 'https://local.invalid').pathname;
    return decodeURIComponent((path.split('/').pop() || '').split('?')[0]);
  } catch {
    return (raw.split('/').pop() || '').split('?')[0];
  }
}

function readablePrintName(...candidates) {
  for (const raw of candidates) {
    const name = stripImageFileExtension(raw);
    if (name && !isOpaqueStorageBaseName(name) && !isGenericListingImageName(name)) return name;
  }
  return '';
}

async function resolvePrintDisplayName(url, nameHint = '', index = 0) {
  const hinted = readablePrintName(nameHint);
  if (hinted) return hinted;
  const fromUrl = readablePrintName(fileNameFromUrl(url));
  if (fromUrl) return fromUrl;
  try {
    const record = await findImageSpaceRecordByUrl(url);
    const fromSpace = readablePrintName(record?.name, record?.fileName, record?.title);
    if (fromSpace) return fromSpace;
  } catch {
    /* 图片空间回表失败时用占位名 */
  }
  return i18n.t('workbench:ipeUtilError.stampLabel', { index: index + 1 });
}

/** 印花图不传 TikTok：只留站内地址和名称拼货号。 */
function printImageStub(url, nameHint = '', index = 0) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return null;
  const canonical = toManagedCanonicalUploadsUrl(absolute);
  const stored = canonical || absolute;
  const name = readablePrintName(nameHint, fileNameFromUrl(stored)) || `印花${index + 1}`;
  return {
    id: `listing-print-${index}`,
    name,
    previewUrl: stored,
    publicUrl: stored,
    ipeUrl: stored,
    url: stored,
    tiktokUrl: '',
    needsSignedPreview: Boolean(canonical) || isManagedStorageUrl(stored),
  };
}

async function imageFromUrl(url, sortHint = 0, nameHint = '', {
  shopId = '',
  imageType = 0,
} = {}) {
  const absolute = toAbsoluteUrl(url);
  if (!absolute) return null;
  const canonical = toManagedCanonicalUploadsUrl(absolute);
  const stored = canonical || absolute;
  let previewUrl = stored;
  if (!stored.startsWith('data:') && !stored.startsWith('blob:') && !canonical) {
    try {
      const fetchable = await resolveFetchableImageUrl(stored);
      if (fetchable) previewUrl = fetchable;
    } catch {
      /* 保留原地址 */
    }
  }
  const name = readablePrintName(nameHint, fileNameFromUrl(stored))
    || stripImageFileExtension(nameHint)
    || stripImageFileExtension(fileNameFromUrl(stored))
    || '';
  let tiktokUrl = '';
  let uri = '';
  let convertedIpe = stored;
  let convertedPublic = stored;
  if (shopId && imageType > 0) {
    const converted = await convertListingImageToTiktok({
      shopId,
      url: stored,
      imageType,
      nameHint: name,
    });
    uri = String(converted?.uri || converted?.tiktokUri || '').trim();
    if (!uri) {
      throw new Error(i18n.t('workbench:ipeUtilError.platformConvertNoUri'));
    }
    tiktokUrl = String(converted?.tiktokUrl || '').trim();
    convertedIpe = converted.ipeUrl || stored;
    convertedPublic = converted.publicUrl || convertedIpe;
    previewUrl = converted.previewUrl || previewUrl;
  }
  return {
    id: `listing-img-${Date.now()}-${sortHint}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    previewUrl,
    publicUrl: convertedPublic,
    ipeUrl: convertedIpe,
    url: tiktokUrl || convertedIpe,
    tiktokUrl,
    uri,
    tiktokUri: uri,
    needsSignedPreview: Boolean(canonical) || isManagedStorageUrl(convertedIpe),
  };
}

function listingColorSlots(listingItem) {
  const slots = resolveListingColorSlots(listingItem);
  return (Array.isArray(slots) ? slots : [])
    .map((slot) => ({
      patternName: String(slot?.patternName || slot?.mainName || '').trim(),
      mainUrl: String(slot?.mainUrl || slot?.main_url || '').trim(),
      mainName: String(slot?.mainName || slot?.patternName || slot?.main_name || '').trim(),
      colorCode: String(slot?.colorCode || slot?.color_code || '').trim(),
      colorLabel: String(slot?.colorLabel || slot?.color_label || '').trim(),
      detailUrls: Array.isArray(slot?.detailUrls)
        ? slot.detailUrls.map((u) => String(u || '').trim()).filter(Boolean)
        : (Array.isArray(slot?.detail_urls)
          ? slot.detail_urls.map((u) => String(u || '').trim()).filter(Boolean)
          : []),
      detailNames: Array.isArray(slot?.detailNames)
        ? slot.detailNames.map((n) => String(n || '').trim())
        : (Array.isArray(slot?.detail_names) ? slot.detail_names.map((n) => String(n || '').trim()) : []),
    }))
    .filter((slot) => slot.mainUrl);
}

function clearSkcPublishImages(skc) {
  return {
    ...skc,
    mainImage: null,
    skuImage: null,
    detailImages: [],
    squareImage: null,
    colorBlockImage: null,
    showroomSlots: {},
  };
}

function imageIdentityKey(image) {
  if (!image) return '';
  return String(
    image.tiktokUrl
    || image.uri
    || image.ipeUrl
    || image.publicUrl
    || image.previewUrl
    || image.url
    || '',
  ).trim().toLowerCase();
}

/**
 * 商品库中文色名 → 英文后再匹配模板（平台变体色多为英文）。
 */
function resolveTiktokListingSlotSkcIndex(skcList, slot, options = {}) {
  let skcIndex = resolveListingSlotSkcIndex(skcList, slot, options);
  if (skcIndex >= 0) return skcIndex;
  const cnLabel = String(slot?.colorLabel || '').trim();
  const enLabel = toTiktokSubmitColorName(cnLabel);
  if (enLabel && enLabel !== cnLabel) {
    skcIndex = resolveListingSlotSkcIndex(
      skcList,
      { ...slot, colorLabel: enLabel },
      options,
    );
  }
  return skcIndex;
}

/** 仅货号拼印花名，不改商家 SKU。 */
function appendPrintNamesToSkcSupplierCode(skc, printNames) {
  if (!printNames.length) return skc;
  return {
    ...skc,
    supplierCode: appendPrintNamesAfterHyphen(
      skc?.supplierCode,
      printNames,
      { maxLen: TIKTOK_SUPPLIER_CODE_MAX_LEN },
    ),
  };
}

/**
 * 从发品表单印花图槽位提取拼接用名称（去扩展名）。
 * @param {object[]} images
 * @returns {string[]}
 */
export function printNamesFromTiktokPrintImages(images) {
  return (Array.isArray(images) ? images : [])
    .map((img, index) => {
      const fromName = readablePrintName(img?.name);
      if (fromName) return fromName;
      const fromUrl = readablePrintName(fileNameFromUrl(
        img?.ipeUrl || img?.publicUrl || img?.url || img?.previewUrl || img?.tiktokUrl || '',
      ));
      return fromUrl || `印花${index + 1}`;
    })
    .filter(Boolean);
}

/**
 * 提交前：弱名（哈希）回表图片空间展示名，再写回 printImages / ipePrintImageNames。
 * @param {object} form
 * @returns {Promise<object>}
 */
export async function enrichTiktokFormPrintNames(form) {
  if (!form || typeof form !== 'object') return form;
  const images = Array.isArray(form.printImages) ? form.printImages : [];
  if (!images.length) return form;
  const nextImages = await Promise.all(images.map(async (img, index) => {
    if (!img || typeof img !== 'object') return img;
    const current = readablePrintName(img.name);
    if (current) return { ...img, name: current };
    const url = img.ipeUrl || img.publicUrl || img.url || img.previewUrl || '';
    const name = await resolvePrintDisplayName(url, '', index);
    return { ...img, name };
  }));
  const names = printNamesFromTiktokPrintImages(nextImages);
  return {
    ...form,
    printImages: nextImages,
    ipePrintImageNames: names,
  };
}

function skcImageUrl(image) {
  if (!image) return '';
  return String(
    image.tiktokUrl
    || image.publicUrl
    || image.previewUrl
    || image.ipeUrl
    || image.url
    || '',
  ).trim();
}

/**
 * 刊登推送：有变体图（skuImage / 兼容 mainImage）即可进入自动规则。
 * @param {object} skc
 * @returns {boolean}
 */
export function skcHasPlatformPublishImages(skc) {
  return Boolean(skcImageUrl(skc?.skuImage) || skcImageUrl(skc?.mainImage));
}

/**
 * @param {object[]} skcList
 * @returns {object[]}
 */
export function filterSkcsReadyForPlatformPublish(skcList) {
  return (Array.isArray(skcList) ? skcList : []).filter(skcHasPlatformPublishImages);
}

function listingColorSlotHasPublishImages(slot) {
  return Boolean(String(slot?.mainUrl || '').trim());
}

function ensureSkcList(form) {
  const list = Array.isArray(form?.skcList) ? [...form.skcList] : [];
  if (list.length === 0) {
    list.push({
      id: `skc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      colorAttributeId: '',
      colorValueId: '',
      colorLabel: '',
      colorIsCustom: false,
      colorExtraValue: '',
      supplierCode: '',
      skcName: '',
      mainImage: null,
      showroomSlots: {},
      detailImages: [],
      squareImage: null,
      colorBlockImage: null,
      skuRows: [],
    });
  }
  return list;
}

function deriveSelectedSizeIds(skcList = []) {
  const ids = [];
  const seen = new Set();
  (skcList || []).forEach((skc) => {
    (skc.skuRows || []).forEach((sku) => {
      const key = skuSizeKey(sku);
      if (key && !seen.has(key)) {
        seen.add(key);
        ids.push(key);
      }
    });
  });
  return ids;
}

/**
 * 商品库标题 → TikTok 商品名称（须英文；Create Product 禁中文）。
 * 含中文则 DeepSeek 译英；已是英文则原样；描述仍用模板预设。
 */
export async function resolveTiktokListingTitles(sourceTitle) {
  const text = String(sourceTitle || '').trim();
  if (!text) {
    return { title: '', titleEn: '' };
  }
  const titleEn = await ensureEnglishListingTitle(text, {
    failMessage: TIKTOK_ENGLISH_TITLE_FAIL_MESSAGE,
  });
  return { title: titleEn, titleEn };
}

/**
 * @param {object} templateForm 商品模板 form 快照
 * @param {object} listingItem 商品库行
 * @param {object} [categoryMeta] { categoryId, productTypeId, categoryPathLabel }
 * @param {{ shopId?: string|number }} [options]
 * @returns {Promise<object>} 合并后的发品 form
 */
export async function applyListingItemToTiktokPublishForm(
  templateForm,
  listingItem,
  categoryMeta = {},
  options = {},
) {
  const shopId = String(options.shopId || '').trim();
  const convertOpts = (imageType) => (shopId ? { shopId, imageType } : {});
  const base = templateForm && typeof templateForm === 'object' ? { ...templateForm } : {};
  const sourceTitle = String(
    resolveListingItemDisplayTitle(listingItem) || listingItem?.title || '',
  ).trim();
  const { title: resolvedTitle, titleEn: resolvedTitleEn } = sourceTitle
    ? await resolveTiktokListingTitles(sourceTitle)
    : { title: '', titleEn: '' };
  const templateTitle = String(base.title || base.productName || '').trim();
  const templateTitleEn = String(base.titleEn || base.englishTitle || '').trim();
  // 标题必须英文：优先商品库译英结果，其次模板英文/无中文标题
  let finalTitle = String(resolvedTitle || '').trim();
  if (!finalTitle || titleContainsCjk(finalTitle)) {
    const fallback = String(resolvedTitleEn || templateTitleEn || '').trim()
      || (!titleContainsCjk(templateTitle) ? templateTitle : '');
    finalTitle = fallback;
  }
  if (finalTitle && titleContainsCjk(finalTitle)) {
    finalTitle = await ensureEnglishListingTitle(finalTitle, {
      failMessage: TIKTOK_ENGLISH_TITLE_FAIL_MESSAGE,
    });
  }
  const finalTitleEn = String(resolvedTitleEn || finalTitle || templateTitleEn || '').trim();

  const mainUrl = carouselUrlsForListingItem(listingItem)[0] || '';
  const detailUrls = detailImageUrlsForListingItem(listingItem);
  const printUrls = printImageUrlsForListingItem(listingItem);
  const initialPrintNames = printImageNamesForListingItem(listingItem);
  const printNames = await Promise.all(
    printUrls.map((url, index) => resolvePrintDisplayName(url, initialPrintNames[index] || '', index)),
  );
  const colorSlots = listingColorSlots(listingItem);
  const fallbackPatternName = readablePrintName(
    listingItem?.mainImageNames?.[0],
    listingItem?.mainImageName,
    fileNameFromUrl(mainUrl),
  );
  const slots = colorSlots.length > 0
    ? colorSlots
    : (mainUrl
      ? [{
        patternName: fallbackPatternName,
        mainName: fallbackPatternName,
        mainUrl,
        detailUrls,
        detailNames: [],
      }]
      : []);

  const templateProductDetails = Array.isArray(base.detailImages) ? base.detailImages.filter(Boolean) : [];
  const templateSkcDetails = ensureSkcList(base).map((skc) => (
    Array.isArray(skc?.detailImages) ? skc.detailImages.filter(Boolean) : []
  ));
  const skcList = ensureSkcList(base).map((skc) => clearSkcPublishImages({ ...skc }));
  const filledIndexes = new Set();
  const usedIndexes = new Set();
  /** @type {{ skcIndex: number, slot: object, variantImage: object|null }[]} */
  const filledBindings = [];

  const fillSkcVariant = async (skcIndex, slot, sortBase) => {
    if (skcIndex < 0 || skcIndex >= skcList.length) return;
    const skc = { ...(skcList[skcIndex] || {}) };
    // 各颜色主图 → 变体图；商品级细节图在下方统一组包，色块不再挂细节
    const variantImage = slot.mainUrl
      ? await imageFromUrl(slot.mainUrl, sortBase, slot.mainName || slot.patternName, convertOpts(1))
      : null;
    if (variantImage) {
      skc.skuImage = variantImage;
      // 兼容旧草稿回退路径（sku_img 优先 skuImage，其次 mainImage）
      skc.mainImage = variantImage;
      skc.squareImage = null;
      skc.colorBlockImage = null;
    }
    skc.detailImages = [];
    if (slot.colorCode) skc.colorCode = slot.colorCode;
    // 模板主规格色名多为英文：不要用商品库中文色名覆盖
    const skuColorCode = extractColorCodeFromSupplierSku(
      (Array.isArray(skc.skuRows) ? skc.skuRows : [])[0]?.supplierSku,
    );
    if (!skc.colorCode && skuColorCode) {
      skc.colorCode = skuColorCode;
    }
    if (!skc.colorCode) {
      const fromNames = extractColorFromImageNames([
        slot.mainName || slot.patternName,
        ...((slot.detailUrls || []).map((url, idx) => (
          String(slot.detailNames?.[idx] || fileNameFromUrl(url) || '').trim()
        ))),
      ]);
      if (fromNames.colorCode) skc.colorCode = fromNames.colorCode;
    }
    skcList[skcIndex] = appendPrintNamesToSkcSupplierCode(skc, printNames);
    if (skcHasPlatformPublishImages(skcList[skcIndex])) {
      filledIndexes.add(skcIndex);
      usedIndexes.add(skcIndex);
      filledBindings.push({ skcIndex, slot, variantImage });
    }
  };

  for (let i = 0; i < slots.length; i += 1) {
    const slot = slots[i];
    if (!listingColorSlotHasPublishImages(slot)) continue;
    let skcIndex = resolveTiktokListingSlotSkcIndex(skcList, slot, { usedIndexes });
    if (skcIndex < 0 && colorSlots.length === 0 && skcList.length === 1) skcIndex = 0;
    if (skcIndex >= 0 && usedIndexes.has(skcIndex)) {
      skcIndex = -1;
    }
    if (skcIndex < 0) {
      throw new Error(formatUnmatchedListingColorMessage(slot));
    }
    // eslint-disable-next-line no-await-in-loop
    await fillSkcVariant(skcIndex, slot, (i + 1) * 20);
  }

  // 商品图组：首色主图 → 主图；其余细节 + 其余色主图 → 其他细节图（受上限）
  let productMainImage = null;
  const productDetailImages = [];
  const seenDetailKeys = new Set();
  const pushDetail = (img) => {
    if (!img || productDetailImages.length >= MAX_DETAIL_IMAGES) return;
    const key = imageIdentityKey(img);
    if (!key || seenDetailKeys.has(key)) return;
    if (productMainImage && key === imageIdentityKey(productMainImage)) return;
    seenDetailKeys.add(key);
    productDetailImages.push(img);
  };

  if (filledBindings.length) {
    const first = filledBindings[0];
    productMainImage = first.variantImage || null;
    // 先各色细节图，再其余颜色主图（与「其他细节图」口径一致）
    for (let bi = 0; bi < filledBindings.length; bi += 1) {
      const { slot } = filledBindings[bi];
      const detailImgs = (
        // eslint-disable-next-line no-await-in-loop
        await Promise.all((slot.detailUrls || []).map((url, idx) => imageFromUrl(
          url,
          (bi + 1) * 100 + idx + 2,
          slot.detailNames?.[idx] || '',
          convertOpts(2),
        )))
      ).filter(Boolean);
      detailImgs.forEach(pushDetail);
      (templateSkcDetails[filledBindings[bi].skcIndex] || []).forEach(pushDetail);
    }
    for (let bi = 1; bi < filledBindings.length; bi += 1) {
      pushDetail(filledBindings[bi].variantImage);
    }
    templateProductDetails.forEach(pushDetail);
  }

  const printImages = printUrls.map((url, idx) => printImageStub(
    url,
    printNames[idx] || '',
    idx,
  )).filter(Boolean);

  const nextSkcList = filterSkcsReadyForPlatformPublish(
    skcList.filter((_, index) => filledIndexes.has(index)),
  );

  return {
    ...base,
    // 描述沿用模板预设（base.description），不覆盖
    title: finalTitle,
    titleEn: finalTitleEn,
    mainImage: productMainImage,
    detailImages: productDetailImages.slice(0, MAX_DETAIL_IMAGES),
    printImages,
    ipePrintImageNames: printNames,
    ipePrintInfringement: resolveListingPrintInfringementForTiktok(listingItem),
    categoryId: categoryMeta.categoryId || base.categoryId || '',
    productTypeId: categoryMeta.productTypeId || base.productTypeId || '',
    categoryPathLabel: categoryMeta.categoryPathLabel || base.categoryPathLabel || '',
    skcList: nextSkcList,
    selectedSizeIds: nextSkcList.length
      ? deriveSelectedSizeIds(nextSkcList)
      : (Array.isArray(base.selectedSizeIds) ? base.selectedSizeIds : []),
  };
}

/**
 * 保存/提交前：把印花图名称接到各 SKC 货号「-」后（多张用 +）；不改商家 SKU。
 * @param {object} form
 * @param {string[]} [printNames]
 */
export function applyPrintNamesToTiktokForm(form, printNames) {
  const fromImages = printNamesFromTiktokPrintImages(form?.printImages);
  const fallbackNames = Array.isArray(form?.ipePrintImageNames)
    ? form.ipePrintImageNames.map((n) => readablePrintName(n)).filter(Boolean)
    : [];
  const names = Array.isArray(printNames) && printNames.length
    ? printNames.map((n) => readablePrintName(n) || String(n || '').trim()).filter(Boolean)
    : (fromImages.length ? fromImages : fallbackNames);
  if (!form || !names.length) return form;
  const skcList = Array.isArray(form.skcList)
    ? form.skcList.map((skc) => ({
      ...skc,
      supplierCode: appendPrintNamesAfterHyphen(
        skc?.supplierCode,
        names,
        { maxLen: TIKTOK_SUPPLIER_CODE_MAX_LEN },
      ),
    }))
    : form.skcList;
  return {
    ...form,
    ipePrintImageNames: names,
    skcList,
  };
}
